import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PencilIcon } from '@heroicons/react/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { Select } from 'chakra-react-select';
import { useEffect, useMemo } from 'react';
import { FileRejection } from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { FormErrors, nationalityOptions, noop } from '@blockpulse3/data/shared';
import {
  DraftShareholder,
  useDeleteResidenceCertificateDocumentMutation,
  useGetResidenceCertificateDocumentQuery,
} from '@blockpulse3/graphql/hooks';
import { formatNumberInt, formatNumberPercentage } from '@blockpulse3/helpers';
import { DropzoneFiles, DropzoneInput, ErrorMessage } from '@blockpulse3/ui/commons';
import { useCompanyShares } from '@blockpulse3/ui/ui-hooks';

import { useSPVForm } from '../../../provider';
import { draftShareholderDefaultValues, naturalShareholderSchema } from '../../../provider/schema';
import { UpdatePreferredSharesPercentageModal } from '../UpdatePreferredSharesPercentagesModal';
import { INaturalShareholderForm, ISharePercentageForm } from '../types';

const DEFAULT_PREFERRED_SHARES_PERCENTAGE = 50;

type Props = {
  /* ** Are fields disabled ** */
  isDisabled?: boolean;
  /* ** Default form values, Natural form version ** */
  defaultValues?: DraftShareholder;
  /* ** Callback of the Cancel button ** */
  onCancel: () => void;
  /* ** Callback of the Submit button ** */
  onSubmit: (data: DraftShareholder) => void;
};

/**
 * NaturalShareholderInformationsForm.
 * Natural version of the ShareholderForm.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function NaturalShareholderInformationsForm({
  isDisabled = false,
  defaultValues = draftShareholderDefaultValues,
  onCancel = noop,
  onSubmit,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nNaturalShareholderInformationsForm = useTranslations(
    'NaturalShareholderInformationsForm',
  );

  const {
    draftId,
    companyDraftContent: { nominalValue, carriedInterest },
  } = useSPVForm();

  /* ** PreferredSharesPercentage field modal ** */
  const { isOpen, onOpen, onClose } = useDisclosure();

  /* ** Delete file mutation ** */
  const [deleteResidenceCertificateDocument] = useDeleteResidenceCertificateDocumentMutation();

  /* ** Shareholder form ** */
  const { register, control, formState, setValue, setError, clearErrors, handleSubmit, watch } =
    useForm<INaturalShareholderForm>({
      defaultValues,
      resolver: yupResolver(naturalShareholderSchema),
      shouldFocusError: false,
    });

  /* ** Fetch previous uploaded residence certificate ** */
  const certificateDocumentReq = useGetResidenceCertificateDocumentQuery({
    variables: {
      companyDraftId: draftId,
    },
    onCompleted: (data) => {
      setValue('residenceCertificate', data.getResidenceCertificateDocument?.id || '');
    },
  });

  /* ** Create file record for the file picker ** */
  const files = useMemo<DropzoneFiles>(() => {
    const certificateDocument =
      certificateDocumentReq.data?.getResidenceCertificateDocument?.document;
    if (certificateDocument) {
      const file = new File([certificateDocument.title], certificateDocument.title);
      return { [certificateDocument.id]: file };
    }
    return {};
  }, [certificateDocumentReq.data]);

  /* ** Watcher for the array of UBOs ** */
  const capitalDeposit = watch('capitalDeposit');
  const preferredSharesPercentage = watch('preferredSharesPercentages');

  /* ** Compute share counts ** */
  const { preferredSharesCount } = useCompanyShares({
    capitalDeposit,
    nominalValue,
    preferredSharesPercentage,
  });

  /* ** Update preferredSharesPercentage ** */
  const handleSharePercentageSubmit = (data: ISharePercentageForm): void => {
    setValue('preferredSharesPercentages', data.percentage);
    onClose();
  };

  /* ** On input change click or drop ** */
  const handleFileUpload = async (
    files: File[],
    fileRejections: FileRejection[],
  ): Promise<void> => {
    clearErrors('residenceCertificate');
    if (fileRejections.length === 0) {
      const formData = new FormData();
      const certificate = files[0];
      /*
       * Fixed file name for validation
       * See: https://www.rfc-editor.org/rfc/rfc2183#section-2.3
       */
      setValue('residenceCertificate', 'file.pdf');

      formData.append('document', certificate);
      formData.append('companyDraftId', draftId);
      await axios.post(
        process.env['NX_API_CONTROLLER_ENDPOINT'] + '/companies/uploadResidenceCertificate',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      certificateDocumentReq.refetch();
    } else {
      const error = fileRejections[0].errors[0].code;
      switch (error) {
        case 'file-too-large': {
          setError('residenceCertificate', { type: 'custom', message: FormErrors.FileTooLarge });
          break;
        }
        default: {
          setError('residenceCertificate', { type: 'custom', message: FormErrors.DropzoneDefault });
          break;
        }
      }
    }
  };

  /* ** On file deletion ** */
  const handleFileDelete = (): void => {
    deleteResidenceCertificateDocument({
      variables: {
        companyDraftId: draftId,
      },
      onCompleted: () => {
        certificateDocumentReq.refetch();
      },
    });
  };

  /* ** Natural shareholder form submit ** */
  const handleFormSubmit: SubmitHandler<INaturalShareholderForm> = async (data) => {
    const { residenceCertificate, ...shareholderData } = data;
    if (preferredSharesPercentage && preferredSharesCount <= 0) {
      setError(
        'capitalDeposit',
        { type: 'custom', message: FormErrors.PreferredShareCount },
        { shouldFocus: true },
      );
    } else {
      onSubmit(shareholderData);
    }
  };

  useEffect(() => {
    /* ** Set preferredSharesPercentage to 5 by default if carried interest case is checked ** */
    if (carriedInterest > 0 && !preferredSharesPercentage) {
      setValue('preferredSharesPercentages', DEFAULT_PREFERRED_SHARES_PERCENTAGE);
    }
  }, [carriedInterest, preferredSharesPercentage, setValue]);

  return (
    <>
      <UpdatePreferredSharesPercentageModal
        isOpen={isOpen}
        defaultValues={{
          percentage: preferredSharesPercentage || DEFAULT_PREFERRED_SHARES_PERCENTAGE,
        }}
        onClose={onClose}
        onSubmit={handleSharePercentageSubmit}
      />
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing="8">
          <Stack spacing="4">
            <FormControl isInvalid={!!formState.errors?.capitalDeposit}>
              <FormLabel htmlFor="capitalDeposit">
                {i18nNaturalShareholderInformationsForm('CapitalDepositField')}
              </FormLabel>
              <InputGroup>
                <Input
                  id="capitalDeposit"
                  type="number"
                  w={{ base: 'full', md: '40%' }}
                  {...register('capitalDeposit')}
                />
                <InputRightAddon>€</InputRightAddon>
                <Text marginLeft="3">
                  {preferredSharesPercentage && capitalDeposit
                    ? i18nNaturalShareholderInformationsForm.rich(
                        'PreferredSharesPercentageField',
                        {
                          preferredSharesPercentages:
                            formatNumberPercentage(preferredSharesPercentage),
                          preferredSharesCount: formatNumberInt(preferredSharesCount),
                          updatePreferredSharesPercentageLink:
                            i18nNaturalShareholderInformationsForm('Modify'),
                          important: (chunks) => <b>{chunks}</b>,
                          link: (chunks) => (
                            <>
                              <button
                                onClick={(e): void => {
                                  e.preventDefault();
                                  onOpen();
                                }}
                              >
                                <u>
                                  <b>{chunks}</b>
                                </u>
                                <Icon as={PencilIcon} color="primary" />
                              </button>
                              <br />
                            </>
                          ),
                        },
                      )
                    : null}
                </Text>
              </InputGroup>
              <ErrorMessage error={formState.errors?.capitalDeposit} />
            </FormControl>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.firstName}>
                <FormLabel htmlFor="firstName">
                  {i18nNaturalShareholderInformationsForm('FirstNameField')}
                </FormLabel>
                <Input
                  id="firstName"
                  isDisabled={isDisabled}
                  type="text"
                  {...register('firstName')}
                />
                <ErrorMessage error={formState.errors?.firstName} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.lastName}>
                <FormLabel htmlFor="lastName">
                  {i18nNaturalShareholderInformationsForm('LastNameField')}
                </FormLabel>
                <Input
                  id="lastName"
                  isDisabled={isDisabled}
                  type="text"
                  {...register('lastName')}
                />
                <ErrorMessage error={formState.errors?.lastName} />
              </FormControl>
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.email}>
                <FormLabel htmlFor="email">
                  {i18nNaturalShareholderInformationsForm('EmailField')}
                </FormLabel>
                <Input id="email" isDisabled={isDisabled} type="text" {...register('email')} />
                <ErrorMessage error={formState.errors?.email} />
              </FormControl>
              <Controller
                control={control}
                name="nationalityData"
                render={({ field }): JSX.Element => (
                  <FormControl isInvalid={!!formState.errors?.nationalityData}>
                    <FormLabel htmlFor="nationalityData">
                      {i18nNaturalShareholderInformationsForm('NationalityField')}
                    </FormLabel>
                    <Select
                      id="nationalityData"
                      isDisabled={isDisabled}
                      menuPlacement="auto"
                      options={nationalityOptions}
                      {...field}
                    />
                    <ErrorMessage error={formState.errors?.nationalityData?.value} />
                  </FormControl>
                )}
              />
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.birthdate}>
                <FormLabel htmlFor="birthdate">
                  {i18nNaturalShareholderInformationsForm('BirthdateField')}
                </FormLabel>
                <Input
                  id="birthdate"
                  isDisabled={isDisabled}
                  type="date"
                  {...register('birthdate')}
                />
                <ErrorMessage error={formState.errors?.birthdate} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.birthplace}>
                <FormLabel htmlFor="birthplace">
                  {i18nNaturalShareholderInformationsForm('BirthplaceField')}
                </FormLabel>
                <Input
                  id="birthplace"
                  isDisabled={isDisabled}
                  type="text"
                  {...register('birthplace')}
                />
                <ErrorMessage error={formState.errors?.birthplace} />
              </FormControl>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Text fontSize="lg" fontWeight="bold">
                {i18nNaturalShareholderInformationsForm('Address.Title')}
              </Text>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.line}>
                    <FormLabel htmlFor="line">
                      {i18nNaturalShareholderInformationsForm('Address.LineField')}
                    </FormLabel>
                    <Input
                      id="line"
                      isDisabled={isDisabled}
                      type="string"
                      {...register('address.line')}
                    />
                    <ErrorMessage error={formState.errors?.address?.line} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.address?.city}>
                    <FormLabel htmlFor="city">
                      {i18nNaturalShareholderInformationsForm('Address.CityField')}
                    </FormLabel>
                    <Input
                      id="city"
                      isDisabled={isDisabled}
                      type="string"
                      {...register('address.city')}
                    />
                    <ErrorMessage error={formState.errors?.address?.city} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.postalCode}>
                    <FormLabel htmlFor="zipcode">
                      {i18nNaturalShareholderInformationsForm('Address.ZipCodeField')}
                    </FormLabel>
                    <Input
                      id="zipcode"
                      isDisabled={isDisabled}
                      type="string"
                      {...register('address.postalCode')}
                    />
                    <ErrorMessage error={formState.errors?.address?.postalCode} />
                  </FormControl>
                  <Controller
                    control={control}
                    name="address.countryData"
                    render={({ field }): JSX.Element => (
                      <FormControl isInvalid={!!formState.errors.address?.countryData}>
                        <FormLabel htmlFor="country">
                          {i18nNaturalShareholderInformationsForm('Address.CountryField')}
                        </FormLabel>
                        <Select isDisabled id="country" menuPlacement="auto" {...field} />
                        <ErrorMessage error={formState.errors.address?.countryData?.value} />
                      </FormControl>
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Text fontSize="lg" fontWeight="bold">
                {i18nNaturalShareholderInformationsForm('FiliationTitle')}
              </Text>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.fatherFirstName}>
                    <FormLabel htmlFor="fatherFirstName">
                      {i18nNaturalShareholderInformationsForm('FatherFirstNameField')}
                    </FormLabel>
                    <Input
                      id="fatherFirstName"
                      isDisabled={isDisabled}
                      type="text"
                      {...register('fatherFirstName')}
                    />
                    <ErrorMessage error={formState.errors?.fatherFirstName} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.fatherLastName}>
                    <FormLabel htmlFor="fatherLastName">
                      {i18nNaturalShareholderInformationsForm('FatherLastNameField')}
                    </FormLabel>
                    <Input
                      id="fatherLastName"
                      isDisabled={isDisabled}
                      type="text"
                      {...register('fatherLastName')}
                    />
                    <ErrorMessage error={formState.errors?.fatherLastName} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.motherFirstName}>
                    <FormLabel htmlFor="motherFirstName">
                      {i18nNaturalShareholderInformationsForm('MotherFirstNameField')}
                    </FormLabel>
                    <Input
                      id="motherFirstName"
                      isDisabled={isDisabled}
                      type="text"
                      {...register('motherFirstName')}
                    />
                    <ErrorMessage error={formState.errors?.motherFirstName} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.motherLastName}>
                    <FormLabel htmlFor="motherLastName">
                      {i18nNaturalShareholderInformationsForm('MotherLastNameField')}
                    </FormLabel>
                    <Input
                      id="motherLastName"
                      isDisabled={isDisabled}
                      type="text"
                      {...register('motherLastName')}
                    />
                    <ErrorMessage error={formState.errors?.motherLastName} />
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>
            <Controller
              control={control}
              name="residenceCertificate"
              render={(): JSX.Element => (
                <FormControl isInvalid={!!formState.errors?.residenceCertificate}>
                  <FormLabel>
                    {i18nNaturalShareholderInformationsForm('ResidenceCertificateField')}
                  </FormLabel>
                  <DropzoneInput
                    files={files}
                    subTitle={i18nNaturalShareholderInformationsForm(
                      'ResidenceCertificateConditions',
                    )}
                    onDelete={handleFileDelete}
                    onDrop={handleFileUpload}
                  />
                  <ErrorMessage error={formState.errors?.residenceCertificate} />
                </FormControl>
              )}
            />
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <Button type="button" variant="secondary" w="full" onClick={onCancel}>
              {i18nGeneral('Back')}
            </Button>
            <Button type="submit" w="full">
              {i18nGeneral('Submit')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </>
  );
}

export type NaturalShareholderInformationsFormProps = Props;
